import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';

import { AudienceDropdownItems, MassMessaging, MessagingEntrypointsDropdownItem } from 'src/app/marketing/shared';
import { DateTimeSchedule } from 'src/app/shared/components/date-time-schedule-modal/shared/models';
import { Choice } from 'src/app/shared/components/select/choice.model';
import DateUtils from 'src/app/shared/utils/date.utils';

@Component({
  selector: 'app-creation-edition-dialog',
  templateUrl: './creation-edition-dialog.component.html',
  styleUrls: ['./creation-edition-dialog.component.scss'],
})
export class CreationEditionDialogComponent implements OnInit {
  audienceDropdownItemsAsChoice: Choice[];

  dialogTitle: string;
  displayDateTimeModal = false;
  disableEdition = false;

  form = this.formBuilder.group({
    name: ['', Validators.required],
    entrypointId: [null, Validators.required],
    accounts: [[]],
    is_test: [{ value: false, disabled: true }],
  });
  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<CreationEditionDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      audienceSize: number;
      audienceDropdownItems: AudienceDropdownItems;
      isNewMassMessaging: boolean;
      massMessaging: MassMessaging;
      massMessagingEntrypointsDropdownItems: MessagingEntrypointsDropdownItem[];
    },
    @Inject('AudienceFactory') private audienceService: any
  ) {}

  ngOnInit(): void {
    const today = moment.utc();

    if (!this.data.massMessaging.audience_filters.dateRange.from) {
      const filters = {
        ...this.data.massMessaging.audience_filters,
        dateRange: {
          from: null,
          to: null,
        },
        expireDateRange: this.data.massMessaging.audience_filters.expireDateRange || {
          from: null,
          to: null,
        },
      };
      filters.dateRange.from = moment(today).year(2000).startOf('year').toDate();
      filters.dateRange.to = today.toDate();
      this.data.massMessaging.audience_filters = this.audienceService.formattedAudienceFilters(filters);
      const range = {
        from: new Date(filters.dateRange.from),
        to: new Date(filters.dateRange.to),
      };
      this.data.massMessaging.rangeString = this.audienceService.dateRangeToString(range);
    }

    this.dialogTitle = this.data.isNewMassMessaging ? 'mass_messaging/create_new' : 'mass_messaging/edit';
    this.disableEdition = !!this.data.massMessaging.status; // undefined or 0 is enabled

    this.audienceDropdownItemsAsChoice = this.data.audienceDropdownItems.sites as Choice[];

    /**
     * The testing feature is allowed only when the audience is less than 15 users.
     */
    if (this.data.audienceSize <= 15) {
      this.form.get('is_test').setValue(true);
      this.form.get('is_test').enable();
    } else {
      this.form.get('is_test').patchValue(this.data.massMessaging.is_test || false);
      this.form.get('is_test').disable();
    }
    this.form.get('name').patchValue(this.data.massMessaging.name);
    this.form.get('accounts').patchValue(this.data.massMessaging.audience_filters.accounts);
    this.form.get('entrypointId').patchValue(this.data.massMessaging.entrypoint_scenario_unique_id);
  }

  /**
   * Schedule callback
   */
  closeDateTimeModal(dateTimeSchedule: DateTimeSchedule) {
    this.displayDateTimeModal = false;
    if (dateTimeSchedule) {
      this.copyFormIntoCurrentMassMessaging();
      const { date, time, timezone } = dateTimeSchedule;
      const scheduledTime = DateUtils.createDate({
        year: date.get('year'),
        month: date.get('month'),
        date: date.get('date'),
        hour: time.h,
        minute: time.m,
        second: 0,
        timezone,
      });
      this.data.massMessaging.scheduledTime = scheduledTime.toISOString();

      this.dialogRef.close(this.data.massMessaging);
    }
  }

  private copyFormIntoCurrentMassMessaging() {
    const { name, is_test, entrypointId } = this.form.value;
    this.data.massMessaging.name = name;
    this.data.massMessaging.entrypoint_scenario_unique_id = entrypointId;
    this.data.massMessaging.is_test = is_test;
  }

  create() {
    this.copyFormIntoCurrentMassMessaging();
    delete this.data.massMessaging.scheduledTime;
    this.dialogRef.close(this.data.massMessaging);
  }

  /**
   * Schedule opens the DateTimeScheduleModalComponent dialog
   * The component manage itself the opening of the dialog, so here we just "display" the component.
   */
  schedule() {
    this.displayDateTimeModal = true;
  }

  cancel() {
    this.dialogRef.close();
  }
}
